import React, { Fragment, useState, useCallback } from 'react';
import {
  Datagrid,
  FunctionField,
  ReferenceManyField,
  TextField,
  useTranslate,
  Filter,
} from 'react-admin';
import Grid from '@material-ui/core/Grid';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { debounce, set as lodashSet } from 'lodash';
import { NoPermissions } from '~/components/ra';
import { StatusField, TextInput as TextInputComponent } from '~/components';
import RaPagination from '~/components/Pagination/RaPagination';
import { perPageDefault, rowsPerPageOptionsDefault } from '~/utils/pagination';
import usePath from '~/hooks/usePath';
import LegalConsentCreate from '../LegalConsents/LegalConsentCreate';
import LegalConsentEdit from '../LegalConsents/LegalConsentEdit';
import { Solution } from '~/types';
import { BooleanInput } from 'react-admin';
import { LegalConsentFilters } from '~/types/LegalConsent';

const label = (key: string) => `resources.legalConsents.fields.${key}`;

interface LegalConsentsTabProps {
  permissions: string[];
  record?: Solution;
}

const LegalConsentsTab = (props: LegalConsentsTabProps) => {
  const { permissions, record: solutionRecord } = props;
  const translate = useTranslate();
  const t = (key: string) => translate(label(key));
  const tu = (key: string) => translate(`utils.${key}`);
  const classes = useStyles();

  const [filters, setFilters] = useState<LegalConsentFilters>({});
  const [showData, setShowData] = useState<any>(null);
  const handleOpen = (record: any) => {
    permissions?.includes('solution.legalConsent.view') && setShowData(record);
  };
  const handleClose = () => setShowData(null);

  if (!permissions) return null;
  if (!permissions.includes('solution.legalConsent.list')) return <NoPermissions />;

  return (
    <Fragment>
      <LegalConsentEdit
        record={showData}
        solutionId={solutionRecord?.id}
        onClose={handleClose}
        filters={filters}
      />
      <Grid container justify='space-between' alignItems='center' className={classes.spacings}>
        <Grid item className={classes.filters}>
          <Filters filters={filters} setFilters={setFilters} />
        </Grid>
        <Grid item>
          <LegalConsentCreate permissions={permissions} solutionRecord={solutionRecord!} />
        </Grid>
      </Grid>
      <ReferenceManyField
        label=''
        reference='legalConsents'
        target='solutions'
        filter={filters}
        sort={{ field: 'created', order: 'DESC' }}
        perPage={perPageDefault}
        pagination={
          <RaPagination
            defaultPerPage={perPageDefault}
            rowsPerPageOptions={rowsPerPageOptionsDefault}
          />
        }
        {...props}
      >
        <Datagrid
          rowClick={(_id: any, _basePath: any, record: any) => handleOpen(record)}
          size='small'
        >
          <TextField source='type' label={t('type')} sortable={false} />
          <TextField source='version' label={t('version')} sortable={false} />
          <FunctionField
            label={t('mandatory')}
            sortable={false}
            render={(record: any) => tu(record?.mandatory ? 'yes' : 'no')}
          />
          <FunctionField
            label={t('validityType')}
            sortable={false}
            render={(record: any) => (record?.validityType ? t(record.validityType) : null)}
          />
          <FunctionField
            label={t('status')}
            sortable={false}
            render={(record: any) => (
              <StatusField status={record?.deleted ? 'ARCHIVED' : 'ACTIVE'} />
            )}
          />
        </Datagrid>
      </ReferenceManyField>
    </Fragment>
  );
};

const Filters = (props: any) => {
  const { record, setFilters } = props;
  const translate = useTranslate();
  const t = (key: string) => translate(label(key));
  const classes = useStyles();
  const { changePath } = usePath();

  const debouncedSetFilters = useCallback(debounce(setFilters, 400), [setFilters]);

  const handleChange = (e: any) => {
    if (!e?.target) return null;
    const { name, value } = e.target;
    changePath({ page: 1 });
    debouncedSetFilters((prev: any) => {
      return { ...lodashSet(prev, name, value) };
    });
  };
  const handleWithDeletedChange = (checked: boolean) => {
    changePath({ page: 1 });
    setFilters((prev: any) => ({ ...prev, withDeleted: checked }));
  };

  return (
    <Filter
      className={classes.form}
      record={record}
      setFilters={() => null}
      hideFilter={() => null}
    >
      <TextInputComponent
        name='type'
        source='type'
        className={classes.formControl}
        label={t('type')}
        alwaysOn
        onChange={handleChange}
        disableHelperText
      />
      <BooleanInput
        name='withDeleted'
        source='withDeleted'
        className={classes.booleanControl}
        label={t('withDeleted')}
        onChange={handleWithDeletedChange}
        alwaysOn
      />
    </Filter>
  );
};

const useStyles = makeStyles((theme) => ({
  spacings: {
    marginBottom: theme.spacing(2),
  },
  form: {
    alignItems: 'center',
    '& > div': {
      padding: 0,
      '& > div:last-child': {
        width: theme.spacing(1),
      },
    },
  },
  formControl: {
    width: 150,
    minWidth: 150,
    maxWidth: 150,
    marginBottom: theme.spacing(1),
  },
  booleanControl: {
    width: 'auto',
    minWidth: 0,
    maxWidth: 'none',
    marginBottom: theme.spacing(1),
    '& .MuiFormControlLabel-root': {
      marginTop: theme.spacing(1),
      marginBottom: 0,
    },
  },
  filters: {
    flex: 1,
  },
}));

export default LegalConsentsTab;
